import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  PadAgreementAddressForm,
  PadAgreementFormSubmitEvent,
  PadAgreementForm,
  PayorBankAccountDetailsForm,
  PayorDetailsForm,
  PaymentMethodFormDetails,
} from '../../models';
import { ACHValidators, ErrorMessages } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';
import { AchInformation } from '@arc/unified-payments-frontend/shared-payment-accounts';

@Component({
  selector: 'payment-add-pad-agreement',
  templateUrl: './add-pad-agreement.component.html',
  styleUrls: ['./add-pad-agreement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPadAgreementComponent implements OnChanges, OnInit {
  @Input() paymentMethod: PaymentMethodFormDetails;
  @Input() isSetInterval: boolean;
  @Output() cancelAddPadAgreement = new EventEmitter();

  @Output() submitAddPadAgreement =
    new EventEmitter<PadAgreementFormSubmitEvent>();

  protected errorMessages: ErrorMessages = {
    exactLength: {
      message: (requiredLength: number) =>
        `${this.translateService.instant(
          'AUREUS_ACH_GROUP_COMPONENT_CHARACTERS_REQUIRED',
          { requiredLength: requiredLength },
        )}`,
      param: 'requiredLength',
    },
  };

  addressGroup: PadAgreementAddressForm = this.fb.group({
    country: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    postalCode: ['', Validators.required],
  });

  bankAddressGroup: PadAgreementAddressForm = this.fb.group({
    country: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    postalCode: ['', Validators.required],
  });

  payorDetailsGroup: PayorDetailsForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    businessName: ['', Validators.required],
    address: ['', Validators.required],
    billingInformation: this.addressGroup,
  });

  payorBankAccountDetailsGroup: PayorBankAccountDetailsForm = this.fb.group({
    institutionName: this.fb.nonNullable.control('', Validators.required),
    transitNumber: this.fb.nonNullable.control('', [
      Validators.required,
      ACHValidators.exactLength(5),
    ]),
    institutionNumber: this.fb.nonNullable.control('', [
      Validators.required,
      ACHValidators.exactLength(3),
    ]),
    bankAccountNumber: this.fb.nonNullable.control('', Validators.required),
  });

  padForm: PadAgreementForm = this.fb.group({
    payorDetails: this.payorDetailsGroup,
    payorBankAccountDetails: this.payorBankAccountDetailsGroup,
  });

  displayModal = true;

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.setupPadAgreementForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.paymentMethod && this.paymentMethod) {
      this.setupPadAgreementForm();
    }
  }

  onCanceled() {
    this.cancelAddPadAgreement.emit();
  }

  onSubmit() {
    if (!this.padForm.valid) {
      this.padForm.markAllAsTouched();
      this.padForm.updateValueAndValidity();
    } else {
      const padAgreementFormSubmitEvent =
        this.buildPadAgreementFormSubmitEvent();
      this.submitAddPadAgreement.emit(padAgreementFormSubmitEvent);
    }
  }

  setupPadAgreementForm() {
    if (this.isSetInterval) {
      this.payorBankAccountDetailsGroup.addControl(
        'bankBillingInformation',
        this.bankAddressGroup,
      );

      this.addRequiredControls(this.payorDetailsGroup, [
        'businessNumber',
        'contactName',
        'contactTitle',
        'contactTelephoneNumber',
      ]);

      this.addRequiredControls(this.payorBankAccountDetailsGroup, [
        'institutionAddress',
        'bankTelephoneNumber',
      ]);
    }

    const paymentInformation = this.paymentMethod
      ?.paymentMethodInformation as AchInformation;

    if (paymentInformation) {
      this.payorBankAccountDetailsGroup.patchValue({
        transitNumber: paymentInformation.transitNumber,
        institutionNumber: paymentInformation.institutionNumber,
        bankAccountNumber: paymentInformation.accountNumber,
      });

      this.payorBankAccountDetailsGroup.controls.transitNumber.disable();
      this.payorBankAccountDetailsGroup.controls.institutionNumber.disable();
      this.payorBankAccountDetailsGroup.controls.bankAccountNumber.disable();
    }
  }

  private addRequiredControls(formGroup: FormGroup, controls: string[]) {
    controls.forEach((controlName) => {
      formGroup.addControl(
        controlName,
        this.fb.control('', Validators.required),
      );
    });
  }

  private buildPadAgreementFormSubmitEvent(): PadAgreementFormSubmitEvent {
    const { billingInformation, ...payorDetails } =
      this.padForm.controls.payorDetails.getRawValue();
    const {
      institutionNumber,
      transitNumber,
      bankBillingInformation,
      ...payorBankAccountDetail
    } = this.padForm.controls.payorBankAccountDetails.getRawValue();

    const padAgreementFormSubmitEvent: PadAgreementFormSubmitEvent = {
      valid: true,
      value: {
        payorDetails: {
          ...payorDetails,
          isoAlpha2Country: billingInformation.country,
          city: billingInformation.city,
          stateOrProvince: billingInformation.state,
          zipCode: billingInformation.postalCode,
        },
        payorBankAccountDetail: {
          ...payorBankAccountDetail,
          institutionNumber: Number(institutionNumber),
          transitNumber: Number(transitNumber),
          ...(bankBillingInformation && {
            isoAlpha2Country: bankBillingInformation.country,
            city: bankBillingInformation.city,
            stateOrProvince: bankBillingInformation.state,
            zipCode: bankBillingInformation.postalCode,
          }),
        },
      },
    };

    return padAgreementFormSubmitEvent;
  }
}
